import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { chatApi } from "./api";
import type { Session } from "./api";

/* ══ 겉창의 상태는 화면 밖에 산다 ══
   화면마다 자기 셸(`AppShell`)을 감싸므로 셸에 상태를 두면 화면을 옮길 때 겉창이 닫힌다.
   그래서 `App` 이 한 번 감싸는 문맥에 두고, 내비바 단추와 겉창이 같은 것을 읽는다.

   여기서 하는 것은 **열림 · 무엇을 보고 있나 · 도는 세션 수**뿐이다. 세션 목록·로그를 읽는
   일은 겉창(`ChatDrawer`)이 열려 있을 때만 한다 — 닫혀 있으면 요청이 0 이라야 한다. */

export type ChatTarget = { taskId?: string; sessionId?: string };

type Dock = {
  open: boolean;
  target: ChatTarget;
  /** 도는 세션 수. **null 은 아직 안 읽었거나 못 읽은 것**이다 — 0 과 다르다. */
  running: number | null;
  openDock: (target?: ChatTarget) => void;
  closeDock: () => void;
  toggleDock: () => void;
  /** 겉창이 읽은 세션 목록으로 단추의 수를 맞춘다 — 두 번 읽지 않는다. */
  reportSessions: (rows: Session[]) => void;
};

const Ctx = createContext<Dock | null>(null);

export function ChatDockProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<ChatTarget>({});
  const [running, setRunning] = useState<number | null>(null);

  const reportSessions = useCallback((rows: Session[]) => {
    setRunning(rows.filter((s) => s.endedAt === null).length);
  }, []);

  /* 단추의 수는 **화면을 열 때와 주소가 바뀔 때** 읽는다. 타이머를 안 둔다 — 닫힌 겉창이
     몇 초마다 API 를 두드리는 것은 「닫히면 요청이 0」의 반대다. 열려 있으면 겉창이 읽은
     목록으로 맞춘다(`reportSessions`). */
  useEffect(() => {
    let alive = true;
    const read = () => {
      chatApi
        .sessions(undefined, 100)
        .then((rows) => {
          if (alive) setRunning(rows.filter((s) => s.endedAt === null).length);
        })
        .catch(() => {
          if (alive) setRunning(null);
        });
    };
    read();
    addEventListener("hashchange", read);
    return () => {
      alive = false;
      removeEventListener("hashchange", read);
    };
  }, []);

  const value = useMemo<Dock>(
    () => ({
      open,
      target,
      running,
      openDock: (t) => {
        if (t) setTarget(t);
        setOpen(true);
      },
      closeDock: () => setOpen(false),
      toggleDock: () => setOpen((v) => !v),
      reportSessions,
    }),
    [open, target, running, reportSessions],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useChatDock(): Dock {
  const v = useContext(Ctx);
  // copy-ignore: 개발자가 부품을 잘못 배치했을 때만 나는 오류다(화면 문구가 아니다).
  if (!v) throw new Error("useChatDock 은 ChatDockProvider 안에서만 쓴다 — App 이 감싼다");
  return v;
}
